import { useState, useCallback } from "react";
import { api } from "@/api/client";
import { ProgressionSheet } from "@/components/rounds/ProgressionSheet";
import { useRoundWorkspace } from "@/components/rounds/useRoundWorkspace";
import type { RoundRow, WorkspaceProps } from "@/components/rounds/model";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
/** Operational entries reuse the progression home; no round settings are copied. */
export function RoundActions(
  props: WorkspaceProps & { roundId: string; complete: boolean; allowAbandon?: boolean },
) {
  const [open, setOpen] = useState(false);
  const [abandon, setAbandon] = useState<{ version: number; guard: () => boolean } | null>(null);
  const [reason, setReason] = useState("");
  const transport = props.transport ?? api;
  const path = `/api/competitions/${props.competitionId}/rounds/${props.roundId}`;
  const read = useCallback(() => transport.get<RoundRow>(path), [transport, path]);
  const state = useRoundWorkspace(props, props.roundId, read);
  return (
    <>
      {props.complete &&
        props.roles.some((role) =>
          ["admin", "producer", "head_judge", "chief_judge"].includes(role),
        ) && (
          <button disabled={!props.manualEnabled} onClick={() => setOpen(true)}>
            Advance round…
          </button>
        )}
      {props.allowAbandon &&
        props.roles.some((role) => ["admin", "chief_judge"].includes(role)) &&
        state.data?.status !== "abandoned" && (
          <button
            disabled={!state.ready || state.busy || !props.manualEnabled}
            onClick={() => {
              setReason("");
              setAbandon({ version: state.data?.version ?? 0, guard: state.capture() });
            }}
          >
            Abandon round
          </button>
        )}
      {state.error && <p role="alert">{state.error}</p>}
      {open && (
        <ProgressionSheet {...props} roundId={props.roundId} onClose={() => setOpen(false)} />
      )}
      <ConfirmDialog
        open={!!abandon}
        title="Abandon this round?"
        confirmLabel="Abandon round"
        onCancel={() => setAbandon(null)}
        onConfirm={async () => {
          if (!reason.trim()) throw new Error("Enter a reason for abandoning this round.");
          if (!abandon?.guard()) throw new Error("The round changed. Refresh and review again.");
          await state.write(
            `${path}/abandon`,
            {
              reason: reason.trim(),
              expected_version: abandon.version,
            },
            "post",
            abandon.guard,
          );
          setAbandon(null);
        }}
      >
        <p>
          Uncompleted heats will be abandoned and their timers stopped. Partial attempts are
          retained and excluded from standings.
        </p>
        <label>
          Reason
          <textarea
            maxLength={2000}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
      </ConfirmDialog>
    </>
  );
}
